import React, { useState } from "react";
import { BOND_CATEGORIES, STRUCTURE_OPTIONS, PORTFOLIO_BUDGET } from "./data.js";

const STEP = 250;

function formatMoney(n) {
  return Math.round(n).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}

const CREDIT_TEXT = { LOW: "text-lime", MEDIUM: "text-gold", HIGH: "text-danger" };

export default function PlanningStage({ onDeploy }) {
  const [allocations, setAllocations] = useState(Object.fromEntries(BOND_CATEGORIES.map((c) => [c.id, 0])));
  const [structure, setStructure] = useState(null);

  const setAmount = (id, value) => setAllocations((prev) => ({ ...prev, [id]: Math.max(0, value) }));

  const totalAllocated = Object.values(allocations).reduce((s, v) => s + v, 0);
  const remaining = PORTFOLIO_BUDGET - totalAllocated;
  const canDeploy = remaining === 0 && !!structure;

  const handleDeploy = () => {
    if (!canDeploy) return;
    onDeploy({ allocations, structure });
  };

  return (
    <div>
      <div className="mb-4 flex items-center justify-between font-display text-[9px] text-mist/60">
        <span>BUILD YOUR BASTION</span>
        <span>BUDGET {formatMoney(PORTFOLIO_BUDGET)}</span>
      </div>

      <p className="mb-5 text-xs text-mist/60">
        Every dollar gets a wall to stand on. Allocate the full budget across the six bond types
        you just scouted — nothing left unassigned.
      </p>

      <div
        className={`mb-6 border-2 p-4 text-center transition-colors ${
          remaining === 0 ? "border-lime bg-lime/10" : remaining > 0 ? "border-gold bg-gold/10" : "border-danger bg-danger/10"
        }`}
      >
        <p className="label-pixel mb-1 text-mist/60">Amount Remaining</p>
        <p className={`font-display text-xl ${remaining === 0 ? "text-lime" : remaining > 0 ? "text-gold" : "text-danger"}`}>
          {formatMoney(remaining)}
        </p>
      </div>

      <div className="mb-6 space-y-3">
        {BOND_CATEGORIES.map((cat) => (
          <div key={cat.id} className="panel p-4">
            <div className="mb-1 flex items-center justify-between">
              <span className="font-display text-[10px] text-mist">
                {cat.icon} {cat.label}
              </span>
              <span className="text-[10px] text-gold">{cat.coupon}% coupon</span>
            </div>
            <p className="mb-3 text-[11px] text-mist/50">{cat.flavor}</p>
            <p className="mb-3 text-[10px] text-mist/40">
              Credit Risk: <span className={CREDIT_TEXT[cat.credit]}>{cat.credit}</span> · Duration: ~{cat.duration}
            </p>
            <div className="flex items-center justify-center gap-3">
              <button
                onClick={() => setAmount(cat.id, allocations[cat.id] - STEP)}
                className="h-7 w-7 border-2 border-black bg-panel2 font-display text-xs text-mist"
              >
                −
              </button>
              <span className="w-24 text-center font-mono text-sm text-mist">{formatMoney(allocations[cat.id])}</span>
              <button
                onClick={() => setAmount(cat.id, allocations[cat.id] + STEP)}
                className="h-7 w-7 border-2 border-black bg-panel2 font-display text-xs text-mist"
              >
                +
              </button>
            </div>
          </div>
        ))}
      </div>

      <section className="panel mb-6 p-4">
        <p className="mb-2 font-display text-[10px] text-cyan">STRUCTURE</p>
        <div className="space-y-2">
          {STRUCTURE_OPTIONS.map((opt) => (
            <button
              key={opt.id}
              onClick={() => setStructure(opt.id)}
              className={`w-full border-2 px-3 py-3 text-left text-[11px] transition-colors ${
                structure === opt.id ? "border-cyan bg-cyan/10 text-cyan" : "border-mist/20 text-mist/70 hover:border-cyan"
              }`}
            >
              <span className="block font-display text-[9px] uppercase">{opt.label}</span>
              <span className="mt-1 block text-mist/50">{opt.description}</span>
            </button>
          ))}
        </div>
      </section>

      <button onClick={handleDeploy} disabled={!canDeploy} className="btn-magenta w-full disabled:opacity-30">
        {canDeploy ? "DEPLOY YOUR BASTION ▶" : "FINISH ALLOCATING TO DEPLOY"}
      </button>
    </div>
  );
}
